import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Row, Col, Alert, Badge, Toast, ToastContainer } from 'react-bootstrap';
import { authService, User } from '../../services/api/authService';
import LoadingSpinner from '../common/LoadingSpinner';

const UserManagement: React.FC = () => {
    const [users, setUsers] = useState<User[]>([]);
    const [roles, setRoles] = useState<string[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [modalError, setModalError] = useState<string | null>(null);

    // Toast notification
    const [toast, setToast] = useState<{ show: boolean; message: string; variant: string }>({
        show: false,
        message: '',
        variant: 'success'
    });

    // User form states
    const [showUserModal, setShowUserModal] = useState<boolean>(false);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [currentUser, setCurrentUser] = useState<User | null>(null);

    // User form fields
    const [firstName, setFirstName] = useState<string>('');
    const [lastName, setLastName] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [confirmPassword, setConfirmPassword] = useState<string>('');
    const [userRoles, setUserRoles] = useState<string[]>([]);
    const [isActive, setIsActive] = useState<boolean>(true);

    // Password reset
    const [showPasswordModal, setShowPasswordModal] = useState<boolean>(false);
    const [passwordUser, setPasswordUser] = useState<User | null>(null);
    const [temporaryPassword, setTemporaryPassword] = useState<string | null>(null);

    // Delete confirmation
    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
    const [userToDelete, setUserToDelete] = useState<User | null>(null);

    // Load users and roles on component mount
    useEffect(() => {
        fetchUsersAndRoles();
    }, []);

    const fetchUsersAndRoles = async () => {
        setLoading(true);
        try {
            const [usersResponse, rolesResponse] = await Promise.all([
                authService.getAllUsers(),
                authService.getAllRoles()
            ]);

            setUsers(usersResponse);
            setRoles(rolesResponse);
            setError(null);
        } catch (err) {
            setError('Failed to load user data. Please try again.');
            console.error('Error fetching user data:', err);
        } finally {
            setLoading(false);
        }
    };

    const resetUserForm = () => {
        setFirstName('');
        setLastName('');
        setEmail('');
        setPassword('');
        setConfirmPassword('');
        setUserRoles([]);
        setIsActive(true);
        setCurrentUser(null);
        setError(null);
        setModalError(null);
    };

    const handleOpenAddUserModal = () => {
        setIsEditing(false);
        setCurrentUser(null);
        setError(null);
        setModalError(null);
        resetUserForm();
        setShowUserModal(true);
    };

    const handleOpenEditUserModal = (user: User) => {
        setIsEditing(true);
        setCurrentUser(user);
        setFirstName(user.firstName);
        setLastName(user.lastName);
        setEmail(user.email);
        setUserRoles(user.roles);
        setIsActive(user.isActive);
        // Clear password fields when editing
        setPassword('');
        setConfirmPassword('');
        setShowUserModal(true);
    };

    const showToast = (message: string, variant: string = 'success') => {
        setToast({
            show: true,
            message,
            variant
        });
    };

    const handleUserSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setError(null);

        try {
            if (isEditing && currentUser) {
                // Update existing user
                await authService.updateUser(currentUser.id, {
                    firstName,
                    lastName,
                    email,
                    roles: userRoles,
                    isActive
                });
                showToast(`User ${firstName} ${lastName} updated successfully.`);
            } else {
                // Create new user
                if (password !== confirmPassword) {
                    setModalError('Passwords do not match.');
                    return;
                }
                if (userRoles.length === 0) {
                    setModalError('No roles selected.');
                    return;
                }
                await authService.createUser({
                    firstName,
                    lastName,
                    email,
                    password,
                    confirmPassword,
                    roles: userRoles,
                    isActive: true
                });
                showToast(`User ${firstName} ${lastName} created successfully.`);
            }

            await fetchUsersAndRoles();
            setShowUserModal(false);
            resetUserForm(); // Reset form after successful submission
        } catch (err: any) {
            setError(err.response?.data?.message || 'An error occurred while saving the user.');
            console.error('Error saving user:', err);
        }
    };

    const handleOpenResetPassword = (user: User) => {
        setPasswordUser(user);
        setPassword('');
        setTemporaryPassword(null);
        setShowPasswordModal(true);
    };

    const handleResetPassword = async () => {
        if (!passwordUser) return;

        try {
            const result = await authService.resetUserPassword(passwordUser.id);
            setTemporaryPassword(result.temporaryPassword);
            showToast(`Password reset successful for ${passwordUser.firstName} ${passwordUser.lastName}.`);
        } catch (err) {
            setError('Failed to reset password. Please try again.');
            showToast('Failed to reset password. Please try again.', 'danger');
            console.error('Error resetting password:', err);
        }
    };

    const handleConfirmDelete = (user: User) => {
        setUserToDelete(user);
        setShowDeleteModal(true);
    };

    const handleDeleteUser = async () => {
        if (!userToDelete) return;

        try {
            await authService.deleteUser(userToDelete.id);
            showToast(`User ${userToDelete.firstName} ${userToDelete.lastName} has been deleted.`);
            await fetchUsersAndRoles();
            setShowDeleteModal(false);
        } catch (err) {
            setError('Failed to delete user. Please try again.');
            showToast('Failed to delete user. Please try again.', 'danger');
            console.error('Error deleting user:', err);
        }
    };

    const handleRoleToggle = (role: string) => {
        setUserRoles(prev =>
            prev.includes(role)
                ? prev.filter(r => r !== role)
                : [...prev, role]
        );
    };

    // Close modal handler with form reset
    const handleCloseModal = () => {
        setShowUserModal(false);
        resetUserForm();
        setError(null);
    };

    return (
        <>
            <Card className="shadow-sm">
                <Card.Header className="bg-white">
                    <div className="d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">User Management</h5>
                        <Button variant="primary" onClick={handleOpenAddUserModal}>
                            <i className="bi bi-person-plus me-1"></i> Add New User
                        </Button>
                    </div>
                </Card.Header>
                <Card.Body>
                    {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}

                    {loading && !showUserModal && <LoadingSpinner centered />}

                    {!loading && users.length === 0 && (
                        <div className="text-center py-5">
                            <p className="text-muted">No users found. Click "Add New User" to create one.</p>
                        </div>
                    )}

                    {!loading && users.length > 0 && (
                        <div className="table-responsive">
                            <Table striped bordered hover>
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Email</th>
                                        <th>Roles</th>
                                        <th>Status</th>
                                        <th>Last Login</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {users.map((user) => (
                                        <tr key={user.id}>
                                            <td>{user.firstName} {user.lastName}</td>
                                            <td>{user.email}</td>
                                            <td>
                                                {user.roles.map(role => (
                                                    <Badge
                                                        key={role}
                                                        bg={role === 'Admin' ? 'danger' : role === 'Manager' ? 'warning' : 'info'}
                                                        className="me-1"
                                                    >
                                                        {role}
                                                    </Badge>
                                                ))}
                                            </td>
                                            <td>
                                                {user.isActive ? (
                                                    <Badge bg="success">Active</Badge>
                                                ) : (
                                                    <Badge bg="secondary">Inactive</Badge>
                                                )}
                                            </td>
                                            <td>
                                                {user.lastLoginDate
                                                    ? new Date(user.lastLoginDate).toLocaleDateString()
                                                    : 'Never'}
                                            </td>
                                            <td>
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    className="me-1"
                                                    title="Edit User"
                                                    onClick={() => handleOpenEditUserModal(user)}
                                                >
                                                    <i className="bi bi-pencil"></i>
                                                </Button>
                                                <Button
                                                    variant="outline-warning"
                                                    size="sm"
                                                    className="me-1"
                                                    title="Reset Password"
                                                    onClick={() => handleOpenResetPassword(user)}
                                                >
                                                    <i className="bi bi-key"></i>
                                                </Button>
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    title="Delete User"
                                                    onClick={() => handleConfirmDelete(user)}
                                                >
                                                    <i className="bi bi-trash"></i>
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </Card.Body>

                {/* Add/Edit User Modal */}
                <Modal
                    show={showUserModal}
                    onHide={handleCloseModal}
                    size="lg"
                    key={`user-modal-${isEditing ? 'edit' : 'add'}-${currentUser?.id || 'new'}`}
                >
                    <Modal.Header closeButton>
                        <Modal.Title>
                            <i className={`bi ${isEditing ? 'bi-person-gear' : 'bi-person-plus'} me-2`}></i>
                            {isEditing ? 'Edit User' : 'Add New User'}
                        </Modal.Title>
                    </Modal.Header>
                    <Form onSubmit={handleUserSubmit}>
                        <Modal.Body>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>First Name*</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={firstName}
                                            onChange={(e) => setFirstName(e.target.value)}
                                            required
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Last Name*</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={lastName}
                                            onChange={(e) => setLastName(e.target.value)}
                                            required
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Form.Group className="mb-3">
                                <Form.Label>Email Address*</Form.Label>
                                <Form.Control
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    autoComplete="new-email"
                                    key={`email-${isEditing ? 'edit' : 'add'}-${currentUser?.id || 'new'}`}
                                    required
                                />
                            </Form.Group>

                            {!isEditing && (
                                <>
                                    <Row>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Password*</Form.Label>
                                                <Form.Control
                                                    type="password"
                                                    value={password}
                                                    onChange={(e) => setPassword(e.target.value)}
                                                    autoComplete="new-password"
                                                    key={`password-${isEditing ? 'edit' : 'add'}-${currentUser?.id || 'new'}`}
                                                    required={!isEditing}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Confirm Password*</Form.Label>
                                                <Form.Control
                                                    type="password"
                                                    value={confirmPassword}
                                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                                    autoComplete="new-password"
                                                    key={`confirm-password-${isEditing ? 'edit' : 'add'}-${currentUser?.id || 'new'}`}
                                                    required={!isEditing}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </>
                            )}

                            <Form.Group className="mb-3">
                                <Form.Label>Roles*</Form.Label>
                                <div className="d-flex flex-wrap gap-2">
                                    {roles.map(role => (
                                        <Form.Check
                                            key={role}
                                            type="checkbox"
                                            id={`role-${role}`}
                                            label={role}
                                            checked={userRoles.includes(role)}
                                            onChange={() => handleRoleToggle(role)}
                                            className="me-3"
                                        />
                                    ))}
                                </div>
                                {userRoles.length}
                            </Form.Group>

                            {isEditing && (
                                <Form.Group className="mb-3">
                                    <Form.Check
                                        type="checkbox"
                                        label="Active"
                                        checked={isActive}
                                        onChange={(e) => setIsActive(e.target.checked)}
                                    />
                                </Form.Group>
                            )}
                            {modalError && <Alert variant="danger" onClose={() => setModalError(null)} dismissible>{modalError}</Alert>}
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={handleCloseModal}>
                                Cancel
                            </Button>
                            <Button variant="primary" type="submit">
                                {loading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-save me-1"></i>
                                        {isEditing ? 'Update User' : 'Create User'}
                                    </>
                                )}
                            </Button>
                        </Modal.Footer>
                    </Form>
                </Modal>

                {/* Reset Password Modal */}
                <Modal show={showPasswordModal} onHide={() => setShowPasswordModal(false)}>
                    <Modal.Header closeButton>
                        <Modal.Title>
                            <i className="bi bi-key me-2"></i>
                            Reset Password
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {passwordUser && (
                            <p>Reset password for user: <strong>{passwordUser.firstName} {passwordUser.lastName}</strong></p>
                        )}

                        {temporaryPassword ? (
                            <>
                                <Alert variant="success">
                                    <p className="mb-2">Password has been reset successfully.</p>
                                    <p className="mb-0">Temporary password: <strong>{temporaryPassword}</strong></p>
                                </Alert>
                                <p className="text-warning">
                                    <i className="bi bi-exclamation-triangle me-1"></i>
                                    <strong>Important:</strong> Please make sure to securely share this temporary password with the user.
                                    They will be required to change it on their first login.
                                </p>
                            </>
                        ) : (
                            <p>
                                <i className="bi bi-info-circle me-1"></i>
                                This will reset the user's password and generate a temporary password.
                                The user will be required to change their password on next login.
                            </p>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowPasswordModal(false)}>
                            Close
                        </Button>
                        {!temporaryPassword && (
                            <Button variant="warning" onClick={handleResetPassword}>
                                <i className="bi bi-key-fill me-1"></i>
                                Reset Password
                            </Button>
                        )}
                    </Modal.Footer>
                </Modal>

                {/* Delete Confirmation Modal */}
                <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                    <Modal.Header closeButton>
                        <Modal.Title>
                            <i className="bi bi-exclamation-triangle me-2 text-danger"></i>
                            Confirm Delete
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {userToDelete && (
                            <>
                                <p>Are you sure you want to delete the following user?</p>
                                <ul>
                                    <li><strong>Name:</strong> {userToDelete.firstName} {userToDelete.lastName}</li>
                                    <li><strong>Email:</strong> {userToDelete.email}</li>
                                </ul>
                                <p className="text-danger">
                                    <i className="bi bi-exclamation-circle me-1"></i>
                                    This action cannot be undone.
                                </p>
                            </>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                            Cancel
                        </Button>
                        <Button variant="danger" onClick={handleDeleteUser}>
                            {loading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Deleting...
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-trash me-1"></i> Delete User
                                </>
                            )}
                        </Button>
                    </Modal.Footer>
                </Modal>
            </Card>

            {/* Toast Container - Fixed position at the top right */}
            <ToastContainer position="top-end" className="p-3" style={{ zIndex: 1050 }}>
                <Toast
                    onClose={() => setToast(prev => ({ ...prev, show: false }))}
                    show={toast.show}
                    delay={3000}
                    autohide
                    bg={toast.variant}
                >
                    <Toast.Header>
                        <strong className="me-auto">
                            {toast.variant === 'success' ? 'Success' : 'Notification'}
                        </strong>
                    </Toast.Header>
                    <Toast.Body className={toast.variant === 'success' ? 'text-white' : ''}>
                        {toast.message}
                    </Toast.Body>
                </Toast>
            </ToastContainer>
        </>
    );
};

export default UserManagement;